import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';

/**
 * The state leadership ladder (state pages) as a seat map: one row per layer of government, top to bottom,
 * and one dot per seat, filled when we know who holds it. Hover or tap a dot for that person; tap a row to
 * list everyone in it beside the map (below it on phones).
 * attrs: ladder ({layers, people}), state ({name, slug}), editor (can add names)
 */
const dateLabel = iso => iso ? new Date(iso).toLocaleDateString('en-GB', {
  month: 'short',
  year: 'numeric'
}) : '';
const short = n => n >= 1e6 ? (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M' : n >= 1e3 ? Math.round(n / 1e3) + 'k' : String(n);
const SHOW = 5; // names listed before searching
const MAX_DOTS = 400; // more seats than this (no state has) would be drawn as a count instead

export default class LeadershipLadder extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.active = 'governor';
    this.query = '';
  }
  pick(key) {
    this.active = key;
    this.query = '';
    // phones: the list sits under the ladder, so bring it into view
    if (window.innerWidth < 900) {
      setTimeout(() => {
        const el = this.element && this.element.querySelector('.DHLadder-panel');
        if (el) window.scrollTo({
          top: el.getBoundingClientRect().top + window.pageYOffset - 90,
          behavior: 'smooth'
        });
      }, 30);
    }
  }

  /** What a step says under its name */
  countText(l) {
    if (l.total === 1) return l.people[0] ? l.people[0].name : 'Not named yet';
    if (l.total) return l.count + ' of ' + l.total + ' named';
    return l.count ? l.count + ' named' : 'None named yet';
  }

  /** Seats to draw: the known total, else as many as are named */
  seats(l) {
    const n = l.total || l.count;
    return n > MAX_DOTS ? 0 : n;
  }

  /** Show who sits in a seat, in a small card by the dot */
  tipAt(ev, l, i) {
    const box = this.element && this.element.querySelector('.DHLadder-map');
    if (!box) return;
    const d = ev.currentTarget.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    this.tip = {
      key: l.key + i,
      person: l.people[i] || null,
      label: l.label,
      x: Math.min(Math.max(d.left - b.left + d.width / 2, 130), b.width - 130),
      y: d.bottom - b.top + 6
    };
  }
  view() {
    // another state opened in the same page: start again at the top
    if (this.slug !== this.attrs.state.slug) {
      this.slug = this.attrs.state.slug;
      this.active = 'governor';
      this.query = '';
      this.tip = null;
    }
    const {
      layers = [],
      people = {}
    } = this.attrs.ladder || {};
    const t = this.tip;
    return m("div", {
      className: "DHLadder"
    }, m("div", {
      className: "DHLadder-map",
      role: "tablist",
      "aria-label": 'Leadership of ' + this.attrs.state.name,
      onmouseleave: () => this.tip = null
    }, layers.map(l => {
      const n = this.seats(l);
      const size = n > 150 ? ' is-tiny' : n > 1 ? '' : ' is-big';
      return m("div", {
        role: "tab",
        tabindex: "0",
        "aria-selected": this.active === l.key ? 'true' : 'false',
        className: 'DHLadder-row' + (this.active === l.key ? ' is-active' : ''),
        onclick: () => this.pick(l.key),
        onkeydown: e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.pick(l.key);
          }
        }
      }, m("div", {
        className: "DHLadder-rowLabel"
      }, m("strong", null, l.label), m("small", null, this.countText(l))), m("div", {
        className: 'DHLadder-dots' + size
      }, n ? Array.from({
        length: n
      }, (_, i) => m("span", {
        className: 'DHLadder-dot' + (l.people[i] ? ' is-named' : '') + (t && t.key === l.key + i ? ' is-hover' : ''),
        onmouseenter: ev => this.tipAt(ev, l, i),
        onclick: ev => {
          this.tipAt(ev, l, i);
          if (this.active !== l.key) this.query = '';
          this.active = l.key;
          if (i >= SHOW && l.people[i]) this.query = l.people[i].name; // beyond the first five: look them up
          ev.stopPropagation();
        }
      })) : m("span", {
        className: "DHMuted DHLadder-noSeats"
      }, "Seat count not loaded yet")));
    }), m("div", {
      role: "tab",
      tabindex: "0",
      "aria-selected": this.active === 'people' ? 'true' : 'false',
      className: 'DHLadder-row is-people' + (this.active === 'people' ? ' is-active' : ''),
      onclick: () => this.pick('people'),
      onkeydown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.pick('people');
        }
      }
    }, m("div", {
      className: "DHLadder-rowLabel"
    }, m("strong", null, "The people"), m("small", null, "Everyone above answers to them")), m("div", {
      className: "DHLadder-peopleLine"
    }, people.population ? m("span", null, m("strong", null, short(people.population)), " residents") : null, people.fromHere ? m("span", null, m("strong", null, people.fromHere.toLocaleString('en-US')), " Voucha members from here") : null)), t ? m("div", {
      className: "DHLadder-tip",
      style: {
        left: t.x + 'px',
        top: t.y + 'px'
      }
    }, t.person ? [m("strong", null, t.person.name), m("small", null, [t.person.title, t.person.place, t.person.party].filter(Boolean).join(' · ') || t.label)] : [m("strong", null, t.label), m("small", null, "Seat not named yet")]) : null), m("div", {
      className: "DHLadder-panel",
      "aria-live": "polite"
    }, this.active === 'people' ? this.peoplePanel(people) : this.layerPanel(layers.find(l => l.key === this.active))));
  }
  layerPanel(l) {
    if (!l) return null;
    const q = this.query.trim().toLowerCase();
    const found = q ? l.people.filter(p => [p.name, p.party, p.place, p.title].join(' ').toLowerCase().includes(q)) : l.people;
    // five at a time; the rest are a search away
    const list = found.slice(0, SHOW);
    const more = found.length - list.length;
    return [m("div", {
      className: "DHLadder-head"
    }, m("i", {
      className: l.icon,
      "aria-hidden": "true"
    }), m("div", null, m("h4", null, l.label), m("p", {
      className: "DHMuted"
    }, l.about))), l.people.length > SHOW ? m("input", {
      className: "FormControl DHLadder-search",
      placeholder: 'Search ' + l.people.length + ' names, parties or places',
      value: this.query,
      oninput: e => this.query = e.target.value
    }) : null, l.people.length ? [m("ul", {
      className: "DHLadder-list"
    }, list.map(p => m("li", null, p.photo ? m("img", {
      src: p.photo,
      alt: "",
      loading: "lazy"
    }) : m("span", {
      className: "DHLadder-initial"
    }, (p.name || '?').charAt(0)), m("span", {
      className: "DHLadder-who"
    }, m("strong", null, p.name), m("small", null, [p.title, p.place, p.since ? 'since ' + dateLabel(p.since) : null].filter(Boolean).join(' · '))), p.party ? m("span", {
      className: "DHLadder-party"
    }, p.party) : null)), !list.length ? m("li", {
      className: "DHMuted"
    }, "No one matches \u201C", this.query.trim(), "\u201D.") : null), more > 0 ? m("p", {
      className: "DHMuted DHLadder-more"
    }, q ? more + ' more match. Narrow the search to see them.' : 'And ' + more + ' more: search to find them.') : null] : m("p", {
      className: "DHMuted DHLadder-empty"
    }, "No names recorded yet for ", this.attrs.state.name, "."), l.total && l.total > 1 && l.people.length && l.people.length < l.total ? m("p", {
      className: "DHMuted DHLadder-note"
    }, l.people.length, " of ", l.total, " named so far.") : null, this.attrs.editor ? m("a", {
      className: "DHLink DHLadder-add",
      href: "/data-hub/editor",
      onclick: e => {
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        m.route.set('/data-hub/editor');
      }
    }, "Add or update names ", m("i", {
      className: "fas fa-arrow-right"
    })) : null];
  }
  peoplePanel(people) {
    return [m("div", {
      className: "DHLadder-head"
    }, m("i", {
      className: "fas fa-people-group",
      "aria-hidden": "true"
    }), m("div", null, m("h4", null, "The people of ", this.attrs.state.name), m("p", {
      className: "DHMuted"
    }, "Everyone above answers to them: they elect the governor, their assembly members, chairpersons and councillors."))), m("div", {
      className: "DHLadder-facts"
    }, people.population ? m("div", null, m("strong", null, people.population.toLocaleString('en-US')), m("small", null, "residents", people.period ? ' (' + people.period + ' estimate)' : '')) : null, people.liveHere !== null && people.liveHere !== undefined ? m("div", null, m("strong", null, people.liveHere.toLocaleString('en-US')), m("small", null, "Voucha members living here")) : null, people.fromHere !== null && people.fromHere !== undefined ? m("div", null, m("strong", null, people.fromHere.toLocaleString('en-US')), m("small", null, "Voucha members from here")) : null), m("a", {
      className: "DHLink",
      href: "/community",
      onclick: e => {
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        m.route.set('/community');
      }
    }, "Join your community ", m("i", {
      className: "fas fa-arrow-right"
    }))];
  }
}